// @ts-check
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getAnalytics } from 'firebase/analytics';
import { FCM } from '../config';
import { WebPushStub } from './web-push-stub';

/** Firebase FCM을 기능을 사용하는 클래스  */
class WebPush {
  /**
   * @param {string} env
   * @param {string} game
   * @param {Record<string, any>} config
   * @param {Record<string, any>} option
   */
  constructor(env, game, config, option) {
    this.env = env || 'DEV';

    if (typeof game !== 'string') throw new Error('No Game Name:');

    this.game = game;

    const topicName = option.topicPrefix[this.game];

    if (!topicName) throw new Error('No FCM Topic Name');

    const app = initializeApp(config);
    this.messaging = getMessaging(app);
    this.analytics = getAnalytics(app);

    this.projectId = `${config.projectId}`;
    this.firebaseVapidKey = option.firebaseVapidKey;
    this.apiEndpoint = option.apiEndpoint;

    this.storageKey = `${this.game}_${option.storageKeySuffix}_${env}`;
    this.topic = `${topicName}_${env}`.toUpperCase();
    this.token = ''; // 유저 FCM 토큰
    this.isSubscribed = false;
  }

  /**
   * 브라우저에 웹푸시 수신 권한을 요구하고 성공여부를 리턴. 에러일 경우 false 리턴.
   * @returns 성공여부
   */
  requestPermission() {
    return Notification.requestPermission()
      .then((permission) => {
        console.log('FCM Permission:', permission);
        return permission === 'granted';
      })
      .catch((error) => {
        console.log('FCM Permission Rejected:', error);
        return false;
      });
  }

  /**
   * 사이트가 foreground 일 경우 메시지 수신 처리
   * background 일 때는 Service Worker에서 처리
   */
  addMessageHandler(callback) {
    return onMessage(this.messaging, (payload) => {
      console.log('FCM Message Received on Foreground:', payload);
      if (!payload) throw new Error('FCM Payload is Empty');

      const context = payload.notification || payload.data;
      const title = context.title || '';
      const body = context.body || '';
      const image = context.image || undefined;
      const icon = 'https://webboard-static.pmang.com/pmang-icon.png';

      // SEE: https://developer.mozilla.org/en-US/docs/Web/API/Notification/Notification
      new Notification(title, {
        body,
        image,
        icon
      });

      if (typeof callback === 'function') {
        callback({
          title,
          body,
          image,
          icon
        });
      }
    });
  }

  hasSubscription() {
    const subscribed = this._loadLocalFcmStatus();
    return Boolean(subscribed && subscribed.topic);
  }

  /** 웹푸시 구독 */
  registerSubscription(topic = '') {
    const currentTopic = topic || this.topic;

    return getToken(this.messaging, {
      vapidKey: this.firebaseVapidKey
    })
      .then((currentToken) => {
        if (!currentToken) {
          throw new Error('FCM Failed to Get Token');
        }
        this.token = currentToken;
        console.log('FCM Token:', this.token);
        return this.token;
      })
      .then(() => {
        const previous = this._loadLocalFcmStatus();

        if (previous.topic === this.topic && previous.token === this.token) {
          console.log('FCM Already Subscribed');
          throw new Error('web-push/already-subscribed');
        }

        // 환경별로 하나의 구독만 유지하게 하기 위해 이전 구독을 취소
        return this.unRegisterSubscription(previous.topic);
      })
      .then(() => {
        return this._registerTopic(currentTopic);
      })
      .catch((error) => {
        // 퍼미션 차단시 로컬스토리지 웹푸시 동의 여부 삭제
        if (error.message.indexOf('messaging/permission-blocked') >= 0) {
          try {
            this._removeLocalFcmStatus();
          } catch (err) {
            // noop
          }
          throw new Error('web-push/permission-blocked');
        } else if (error.message.indexOf('web-push/already-subscribed') >= 0) {
          return true; // 이미 구독중이라면 그냥 성공
        } else {
          console.log('FCM Subscribing Error:', error.name, '-', error.message);
        }

        return false; // 명시적으로 유저가 차단한 상황이 아니라면 그냥 조용히 실패 처리
      });
  }

  /** 푸시토큰을 삭제하고, 토픽 구독 취소한 후 성공여부를 리턴 */
  unRegisterSubscription(topic = '') {
    const currentTopic = topic || this.topic;

    return this._unRegisterTopic(currentTopic).catch((error) => {
      console.log('FCM Cancel Subscription Error:', error);
      return false;
    });
  }

  // PRIVATE /////////////////////////////////////////////////////////////////////

  _loadLocalFcmStatus() {
    try {
      const status = localStorage.getItem(this.storageKey);
      const { topic, token } = JSON.parse(status);
      return { topic, token };
    } catch (err) {
      return { topic: '(none)', token: '(none)' };
    }
  }

  _saveLocalFcmStatus(topic) {
    localStorage.setItem(this.storageKey, JSON.stringify({ topic, token: this.token }));
  }

  _removeLocalFcmStatus() {
    localStorage.removeItem(this.storageKey);
  }

  /** Firebase 토픽을 등록하고 결과를 리턴 */
  _registerTopic(topic) {
    return fetch(this.apiEndpoint, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        topic: topic,
        tokens: [this.token]
      })
    })
      .then((response) => {
        if (response.status < 200 || response.status >= 400) {
          console.log('FCM Registering Topic Error: ' + response.status + ' - ' + response.text());
          return false;
        }

        this._saveLocalFcmStatus(topic);
        this.isSubscribed = true;
        console.log('FCM Topic Registered:', topic);
        return true;
      })
      .catch((error) => {
        console.log('FCM Registering Topic Error:', error);
        return false;
      });
  }

  /** 웹푸시 토픽 구독을 취소하고 결과를 리턴 */
  _unRegisterTopic(topic) {
    return fetch(this.apiEndpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        topic: topic,
        tokens: [this.token]
      })
    })
      .then((response) => {
        if (response.status < 200 || response.status >= 400) {
          console.error(
            'FCM Un-registering Topic Error: ' + response.status + ' - ' + response.text()
          );
          return false;
        }

        this._removeLocalFcmStatus();
        this.isSubscribed = false;
        console.log('FCM Topic Un-registered:', topic);
        return true;
      })
      .catch((error) => {
        console.error('FCM Un-registering Topic Error:', error);
        return false;
      });
  }
}

let fcmInstance;

/**
 * @param {string} env
 * @param {string} game
 * @returns {WebPush | WebPushStub}
 */
export function createWebPushManager(env, game) {
  if (fcmInstance) {
    return fcmInstance;
  }

  const fcmSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

  if (fcmSupported) {
    fcmInstance = new WebPush(env, game, FCM.firebaseConfig, FCM.fcmOptions);
  } else {
    fcmInstance = new WebPushStub();
  }

  return fcmInstance;
}
